import React, { useState, useEffect, useCallback, useRef, createContext } from "react";
import { Auth } from "aws-amplify";
import useNotifications from "../hooks/useNotifications";
import useTheme from "../hooks/useTheme";
import AppBackService from "../services/appback";

export const RootContext = createContext({
  isAuthenticated: false,
  setIsAuthenticated: () => {},
  authStatus: false,
  setAuthStatus: () => {},
  accountStatus: false,
  setAccountStatus: () => {},
  accountStatusName: "",
  setAccountStatusName: () => {},
  accountLength: 0,
  setAccountLength: () => {},
  hasTimeOut: false,
  setHasTimeOut: () => {},
});

const RootProvider = (props) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasTimeOut, setHasTimeOut] = useState(false);
  const [authStatus, setAuthStatus] = useState(null);
  const [authAttrs, setAuthAttrs] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [authCode, setAuthCode] = useState(new URLSearchParams(window.location.search).get("code"));
  const [inZoomClient, setInZoomClient] = useState(false);
  const [runningContext, setRunningContext] = useState(null);
  const [loggingIn, setLoggingIn] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [accountStatusName, setAccountStatusName] = useState("");
  const [accountLength, setAccountLength] = useState(0);
  const { notifInterval, displayNotifs } = useNotifications(userInfo, notifications, setNotifications);
  const { theme, toggleTheme, setTheme } = useTheme();

  // Reads the Amplify session into the app's auth state. Runs on mount and
  // on auth changes (below); the Zoom sign-in calls it directly once it has
  // created a session (TT-1208), since nothing else would notice one appear.
  // The auth status the last load settled on: its own setAuthStatus must not
  // count as a new trigger.
  const settled = useRef(undefined);
  const loadSession = useCallback(async () => {
    try {
      const cogUser = await Auth.currentAuthenticatedUser();
      setUserInfo(cogUser);

      setLoggingIn(true);
      const User = await AppBackService.getUser(cogUser.username);

      setLoggingIn(false);
      // Create the User and Account DB objects if needed
      if (!User.id) {
        console.log("Creating User");

        //Check to see if the user has a saved zoom authorziation code indicating they came via zoom oauth
        const ZoomAuth = await AppBackService.getZoomAuthCode(cogUser.username);

        // A non-Zoom (web) signup has no auth code: get_auth_code returns { auth: null }.
        // Guard the deref — an unguarded ZoomAuth.auth.code throws for every new web
        // user, which the catch below swallows into a logout before insertUser runs,
        // permanently stranding the account at the login page.
        await AppBackService.insertUser(ZoomAuth?.auth?.code ? "zoom" : "web");
      }

      setAuthAttrs(cogUser.attributes);
      settled.current = true;
      setAuthStatus(true);
    } catch (e) {
      settled.current = false;
      setAuthStatus(false);
    }
  }, []);

  // One load at a time, and none lost: a trigger that arrives while a load
  // runs (a sign-in right after logout's) runs once more after it. A time
  // window here once dropped the login's load and left the app on its
  // skeleton (TT-1253); overlapping loads could insert a new user twice.
  const loading = useRef(false);
  const again = useRef(false);
  const requestSession = useCallback(async () => {
    if (loading.current) {
      again.current = true;
      return;
    }
    loading.current = true;
    try {
      do {
        again.current = false;
        await loadSession();
      } while (again.current);
    } finally {
      loading.current = false;
    }
  }, [loadSession]);

  const codeSeen = useRef(undefined);
  useEffect(() => {
    if (authStatus === settled.current && authCode === codeSeen.current) return;
    codeSeen.current = authCode;
    requestSession();
  }, [authCode, authStatus, requestSession]);

  return (
    <RootContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        authStatus,
        setAuthStatus,
        loadSession,
        authAttrs,
        setAuthAttrs,
        userInfo,
        setUserInfo,
        authCode,
        setAuthCode,
        accountStatusName,
        setAccountStatusName,
        accountLength,
        setAccountLength,
        hasTimeOut,
        setHasTimeOut,
        inZoomClient,
        setInZoomClient,
        runningContext,
        setRunningContext,
        loggingIn,
        notifications,
        setNotifications,
        notifInterval,
        displayNotifs,
        theme,
        toggleTheme,
        setTheme,
      }}>
      {props.children}
    </RootContext.Provider>
  );
};

export default RootProvider;
